import { useDocumentTitle } from '../../../common/useDocumentTitle'
import { Col, Container, Form, Row, Spinner, Stack } from 'react-bootstrap'
import React, { useEffect, useState } from 'react'
import './Settings.scss'
import { useZocksQuota } from '../../../components/ScopedBlock/hooks'
import { ScopedBlock } from '../../../components/ScopedBlock/ScopedBlock'
import { MissingScopeAction } from '../../../components/ScopedBlock/models'
import { Button } from '../../../components/Button/Button'
import PrizeBadge from '../../../components/ScopedBlock/PrizeBadge.svg?url'
import { useNavigate } from 'react-router-dom'
import { SelectDropdown } from '../../../components/SelectDropdown/SelectDropdown'
import { useGetAllServiceLocationsQuery } from '../../../store/user/user'
import { IntegrationServiceId, supportedCallSolutions } from '../../../common/integrations'
import { useAppSelector } from '../../../store/hooks'
import { selectAccountId } from '../../../store/auth/authSlice'
import PhoneInput, { formatPhoneNumber } from 'react-phone-number-input'
import { useGetAccountQuery, useUpdateAccountMutation } from '../../../store/accountUserSettings/accountUserSettings'
import { MeetingProviderAccountIntegrationConfig } from '../../../models/Account'

export const PhoneSettings: React.FC = () => {
  useDocumentTitle('Setup')
  const navigate = useNavigate()
  const { hasQuota } = useZocksQuota()
  const sharedPhoneLineAvailable = hasQuota('integration.sharedPhoneLine.value')
  const accountId = useAppSelector(selectAccountId)
  const { data: account, isFetching: isAccountFetching } = useGetAccountQuery({ accountId }, { refetchOnMountOrArgChange: true })
  const [updateAccount, { isLoading: isUpdating }] = useUpdateAccountMutation()
  const [sharedPhoneLineEnabled, setSharedPhoneLineEnabled] = useState<boolean>(false)
  const [defaultProvider, setDefaultProvider] = useState<string | null>(null)
  const [defaultLocation, setDefaultLocation] = useState<string | null>(null)
  const [allowedExternalIds, setAllowedExternalIds] = useState<string[]>([])
  const [addingPhoneNumber, setAddingPhoneNumber] = useState<boolean>(false)
  const [tmpPhoneNumber, setTmpPhoneNumber] = useState<string | undefined>('')
  const { data: locations, isFetching: locationsFetching } = useGetAllServiceLocationsQuery({
    accountId: accountId,
    serviceId: IntegrationServiceId.PHONE,
  })

  const phoneIntegration = React.useMemo(() => {
    if (!account?.data?.effectiveConfig?.integrations) {
      return null
    }
    return account.data.effectiveConfig.integrations.find((integration) => integration.type === 'meeting_provider' && integration.serviceId === 'phone') as
      | MeetingProviderAccountIntegrationConfig
      | undefined
  }, [account])

  const enterprisePhoneIntegration = React.useMemo(() => {
    if (!account?.data?.enterpriseConfig?.integrations) {
      return null
    }
    return account.data.enterpriseConfig.integrations.find((integration) => integration.type === 'meeting_provider' && integration.serviceId === 'phone') as
      | MeetingProviderAccountIntegrationConfig
      | undefined
  }, [account])

  const isEnterpriseManaged = enterprisePhoneIntegration != null && enterprisePhoneIntegration.sharedPhoneLineEnabled != null
  const isProviderEnforced = enterprisePhoneIntegration != null && enterprisePhoneIntegration.defaultProvider != null

  useEffect(() => {
    if (!phoneIntegration) {
      return
    }
    setSharedPhoneLineEnabled(phoneIntegration.sharedPhoneLineEnabled ?? false)
    setDefaultProvider(phoneIntegration.defaultProvider)

    setDefaultLocation(phoneIntegration.defaultLocation)

    setAllowedExternalIds(phoneIntegration.allowedExternalIds)
  }, [phoneIntegration])

  const savePhoneAccountIntegrationConfig = (
    sharedPhoneLineEnabled: boolean | null,
    defaultProvider: string | null,
    defaultLocation: string | null,
    allowedExternalIds: string[],
  ) => {
    if (!account) {
      return
    }
    const phoneIntegration: MeetingProviderAccountIntegrationConfig = {
      type: 'meeting_provider',
      serviceId: 'phone',
      sharedPhoneLineEnabled,
      allowedExternalIds,
      defaultProvider,
      defaultLocation,
    }
    const integrations = [...(account.data.effectiveConfig.integrations || []).filter((integration) => integration.serviceId !== 'phone'), phoneIntegration]
    const accountConfig = { ...account.data.config, integrations }
    updateAccount({
      accountId: accountId,
      payload: {
        config: accountConfig,
      },
    })
  }

  const handleSharedPhoneLineChanged = (enabled: boolean) => {
    if (isEnterpriseManaged) {
      return
    }
    setSharedPhoneLineEnabled(enabled)
    savePhoneAccountIntegrationConfig(enabled, defaultProvider, defaultLocation, allowedExternalIds)
  }
  const handleDefaultProviderSet = (provider: string | null) => {
    if (isProviderEnforced) {
      return
    }
    setDefaultProvider(provider)
    savePhoneAccountIntegrationConfig(sharedPhoneLineEnabled, provider, defaultLocation, allowedExternalIds)
  }

  const handleDefaultLocationSet = (location: string) => {
    setDefaultLocation(location)
    savePhoneAccountIntegrationConfig(sharedPhoneLineEnabled, defaultProvider, location, allowedExternalIds)
  }
  const handleRemoveExternalId = (phoneNumber: string) => {
    if (isEnterpriseManaged && !sharedPhoneLineEnabled) {
      return
    }
    const externalIds = allowedExternalIds.filter((externalId) => externalId !== phoneNumber)
    setAllowedExternalIds(externalIds)
    savePhoneAccountIntegrationConfig(sharedPhoneLineEnabled, defaultProvider, defaultLocation, externalIds)
  }

  const handleOnCancelAddingPhone = () => {
    setTmpPhoneNumber('')
    setAddingPhoneNumber(false)
  }

  const handleOnSaveAddingPhone = () => {
    if (tmpPhoneNumber && !allowedExternalIds.includes(tmpPhoneNumber)) {
      const externalIds = [...allowedExternalIds, tmpPhoneNumber]
      setAllowedExternalIds(externalIds)
      savePhoneAccountIntegrationConfig(sharedPhoneLineEnabled, defaultProvider, defaultLocation, externalIds)
    }
    setTmpPhoneNumber('')
    setAddingPhoneNumber(false)
  }

  return (
    <>
      <Container fluid className='p-0'>
        <Row className='zocks-mb-2'>
          <Col className='p-0'>
            <h4 className='zocks-text-gray-900'>Phone Integration</h4>
          </Col>
        </Row>
        {(isEnterpriseManaged || isProviderEnforced) && (
          <Row className='zocks-mb-2 p-3 zocks-bg-color-primary-light gap-3 justify-content-center rounded'>
            <Col className='d-flex align-items-center zocks-gap-2.5 align-self-center'>
              <div className='d-flex align-self-center gap-3 align-items-center'>
                <i className='bi bi-info-square zocks-text-primary consent-settings-info-square' />
                <span className='zocks-text-gray-900'>Phone integration settings are managed at the enterprise level and cannot be modified here.</span>
              </div>
            </Col>
          </Row>
        )}
        <Row className='zocks-mb-2 p-3 zocks-bg-color-primary-light gap-3 justify-content-center rounded'>
          <Col className='d-flex align-items-center zocks-gap-2.5 align-self-center'>
            <Stack direction='horizontal' gap={3}>
              <div className='d-flex align-self-center gap-3 align-items-center'>
                <i className='bi bi-info-square zocks-text-primary consent-settings-info-square' />
                <span className='zocks-text-gray-900'>
                  By default it is recommended to use the Phone Linking option as this enables you to permanently add the AI Assistant to your contacts list. If
                  you use a shared line in the office or a VoIP / UCaaS based software solution like RingCentral please select it from the dropdown below.
                </span>
              </div>
              {!sharedPhoneLineAvailable && (
                <ScopedBlock
                  scope={{
                    scope: 'billing_subscription:change',
                    superScope: ['billing_subscription:admin'],
                  }}
                  missingScopeAction={MissingScopeAction.DISABLE}
                >
                  <Button
                    className='d-flex p-1 p-2 align-items-center text-nowrap'
                    variant='primary'
                    onClick={() => {
                      navigate('/account/plan')
                    }}
                  >
                    <img alt='Upgrade' src={PrizeBadge} />
                    Get feature
                  </Button>
                </ScopedBlock>
              )}
            </Stack>
          </Col>
        </Row>
        <Row className='gap-3 p-0 align-items-center zocks-mb-3'>
          <Col md='auto' className='p-0'>
            <ScopedBlock
              scope={{
                scope: 'account:update',
                superScope: ['account:admin'],
              }}
              missingScopeAction={MissingScopeAction.DISABLE}
            >
              <Form.Switch
                disabled={isAccountFetching || !sharedPhoneLineAvailable || isUpdating || isEnterpriseManaged}
                checked={sharedPhoneLineEnabled}
                onChange={(e) => handleSharedPhoneLineChanged(e.currentTarget.checked)}
              />
            </ScopedBlock>
          </Col>
          <Col className='p-0'>
            <div className='paragraph-medium'>Enable Shared Phone Line support on the entire account</div>
          </Col>
        </Row>
        <Row className='zocks-mb-1.5'>
          <Col className='p-0'>
            <h4 className='zocks-text-gray-900'>Set your default configuration</h4>
          </Col>
        </Row>
        <Row className='zocks-mb-0'>
          <Col className='p-0'>
            <div className='lead-medium'>Call Solutions</div>
          </Col>
        </Row>
        <Row className='zocks-mb-2'>
          <Col className='p-0'>
            <div className='paragraph-medium-extended'>
              Zocks provides time limited extension codes that you can use if you have a shared line with the same outgoing number, use a software based phone
              solution or if you can’t link your phone number to your account.
            </div>
          </Col>
        </Row>
        <Row className='zocks-mb-2'>
          <Col className='p-0'>
            <div className='paragrap-medium bold'>Select your call solution</div>
            <div className='flex-grow-1'>
              <ScopedBlock
                scope={{
                  scope: 'account:update',
                  superScope: ['account:admin'],
                }}
                missingScopeAction={MissingScopeAction.DISABLE}
              >
                <SelectDropdown
                  placeholder='Your call solution platform type'
                  disabled={!sharedPhoneLineAvailable || !sharedPhoneLineEnabled || isUpdating || isProviderEnforced}
                  options={supportedCallSolutions.map(({ id, displayName }) => ({
                    id,
                    label: displayName,
                  }))}
                  onOptionSelected={(option) => {
                    handleDefaultProviderSet(option.id)
                  }}
                  initialSelectedOption={
                    supportedCallSolutions
                      .map(({ id, displayName }) => ({
                        id,
                        label: displayName,
                      }))
                      .find((option) => option.id === defaultProvider) ?? null
                  }
                  showDeselectOption={true}
                  deselectOptionLabel='Clear'
                  onDeselect={() => handleDefaultProviderSet(null)}
                />
              </ScopedBlock>
            </div>
          </Col>
        </Row>
        <Row className='zocks-mb-2'>
          <Col className='p-0'>
            <div className='paragrap-medium bold'>Choose your preferred AI Assistant number</div>
            <div className='flex-grow-1'>
              {locationsFetching ? (
                <div className='d-flex align-items-center justify-content-center'>
                  <Spinner />
                </div>
              ) : (
                <ScopedBlock
                  scope={{
                    scope: 'account:update',
                    superScope: ['account:admin'],
                  }}
                  missingScopeAction={MissingScopeAction.DISABLE}
                >
                  <SelectDropdown
                    placeholder='Select region'
                    disabled={!sharedPhoneLineAvailable || !sharedPhoneLineEnabled || isUpdating}
                    options={
                      locations?.data && locations.data.length > 0
                        ? locations.data.map(({ id, displayName, locationDetails: { phoneNumber } }) => ({
                            id,
                            label: `${displayName}: ${formatPhoneNumber(phoneNumber)}`,
                          }))
                        : []
                    }
                    onOptionSelected={(option) => {
                      handleDefaultLocationSet(option.id)
                    }}
                    initialSelectedOption={
                      locations?.data && locations.data.length > 0
                        ? (locations.data
                            .map(({ id, displayName, locationDetails: { phoneNumber } }) => ({
                              id,
                              label: `${displayName}: ${formatPhoneNumber(phoneNumber)}`,
                            }))
                            .find((option) => option.id === defaultLocation) ?? null)
                        : null
                    }
                  />
                </ScopedBlock>
              )}
            </div>
          </Col>
        </Row>
        <Row className='zocks-mb-2'>
          <Col className='p-0'>
            <div className='paragraph-medium bold'>
              You can add an extra level of security by only allowing your office number to add the AI assistant to your calls. This a filtering option that you
              can use to specify the phone numbers that are approved by you.
            </div>
          </Col>
        </Row>
        {allowedExternalIds.map((phoneNumber) => (
          <Row key={phoneNumber} className='border zocks-border-color-gray-200 zocks-border-0.25 py-3 px-2 zocks-mb-0.75'>
            <Col>
              <div className='border-1 zocks-border-color-gray-200 zocks-border-0.25'>{formatPhoneNumber(phoneNumber)}</div>
            </Col>
            <Col xs='auto'>
              <ScopedBlock
                scope={{
                  scope: 'account:update',
                  superScope: ['account:admin'],
                }}
                missingScopeAction={MissingScopeAction.DISABLE}
              >
                <i role='button' className='bi bi-trash3' onClick={() => !isUpdating && handleRemoveExternalId(phoneNumber)} />
              </ScopedBlock>
            </Col>
          </Row>
        ))}
        {addingPhoneNumber ? (
          <>
            <Row className='mt-3'>
              <Col>
                <div className='label-small'>Phone number*</div>
              </Col>
            </Row>
            <Row className='zocks-mb-1'>
              <Col>
                <PhoneInput
                  placeholder='e.g. (800) 555-0175'
                  defaultCountry='US'
                  countryOptionsOrder={['US', 'CA', '...']}
                  international={true}
                  inputComponent={Form.Control}
                  onChange={setTmpPhoneNumber}
                  disabled={isUpdating}
                />
              </Col>
            </Row>
            <Row>
              <Col>
                <Stack direction='horizontal' className='d-flex gap-2 justify-content-end'>
                  <Button className='button-small' variant='secondary' onClick={handleOnCancelAddingPhone}>
                    Cancel
                  </Button>
                  <Button className='button-small' variant='primary' disabled={isUpdating} onClick={handleOnSaveAddingPhone}>
                    Save
                  </Button>
                </Stack>
              </Col>
            </Row>
          </>
        ) : (
          <Row>
            <Col>
              <ScopedBlock
                scope={{
                  scope: 'account:update',
                  superScope: ['account:admin'],
                }}
                missingScopeAction={MissingScopeAction.DISABLE}
              >
                <Button
                  disabled={!sharedPhoneLineAvailable || !sharedPhoneLineEnabled || isUpdating}
                  className='text-decoration-none justify-content-start zocks-text-primary button-small'
                  variant='link'
                  onClick={() => setAddingPhoneNumber(true)}
                >
                  + Add phone number
                </Button>
              </ScopedBlock>
            </Col>
          </Row>
        )}
      </Container>
    </>
  )
}
